/**
 * @module analyticsManager
 */
import { localUserId } from "../store";
import isBrowser from "../../utils/isBrowser";

/**
 * Initializes Google Analytics library.
 */
function loadGA() {
  (function(i, s, o, g, r, a, m) {
    i["GoogleAnalyticsObject"] = r;
    (i[r] =
      i[r] ||
      function() {
        (i[r].q = i[r].q || []).push(arguments);
      }),
      (i[r].l = 1 * new Date());
    (a = s.createElement(o)), (m = s.getElementsByTagName(o)[0]);
    a.async = 1;
    a.src = g;
    m.parentNode.insertBefore(a, m);
  })(window, document, "script", "https://www.google-analytics.com/analytics.js", "ga");
}

/**
 * Runs Google Analytics global method with arguments.
 * @param  {...any} args - Method passed arguments.
 */
const runGA = (...args) => {
  if (isBrowser && !window.ga) {
    console.warn("[GA] initialize method must be called first or Google Analytics should be loaded manually");
    return false;
  }
  isBrowser && window.ga(...args);
};

/**
 * Runs Google Analytics command ("send", "set"...).
 * @param  {...any} args - Method passed arguments.
 */
function runCommand(...args) {
  const command = args[0];
  if (typeof command !== "string") {
    console.warn("[GA] ga command must be a string");
    return false;
  }
  runGA(...args);
}

/**
 * Initializes Google Analytics tracking with provided account ID.
 * @param {String} trackingId - GA Tracking Account ID.
 */
function initializeTracking(trackingId) {
  if (!trackingId) {
    console.warn("[GA] trackingId isº required to initialize Google Analytics");
    return false;
  }
  runCommand("create", trackingId, "auto");
}

/**
 * Runs "send" command by passing a tracking related data object.
 * @param {Object} fieldsObject - Object of fields to be tracked.
 */
function send(fieldsObject) {
  if (!fieldsObject || (Object.keys(fieldsObject) && Object.keys(fieldsObject).length === 0)) {
    console.warn("[GA] fieldsObject is required in send() and cannot be an empty object");
    return false;
  }
  runCommand("send", fieldsObject);
}

/**
 * Sets custom dimensions.
 * @param {String} pageType - Page type.
 * @param {String} pageName - Page name.
 * @param {String} titleId - Title ID.
 */
async function setCustomDimensions(pageType, pageName, titleId = null) {
  const userId = await localUserId.get.call(this);
  const customDimensionData = {
    dimension3: this.lang,
    dimension9: this.parentalLevel,
    dimension10: pageType,
    dimension13: pageName,
    ...(userId && {
      userId: userId,
      dimension1: userId
    }),
    ...(titleId && { dimension14: titleId })
  };
  this.set(customDimensionData);
}

export default {
  /**
   * Initializes GA tracking engine.
   */
  initialize() {
    if (!isBrowser) {
      return false;
    }
    if (!window.ga) {
      loadGA();
    }
    initializeTracking(this.analyticsId);
  },

  /**
   * Runs "set" command by passing a tracking related data object.
   * @param {Object} fieldsObject - Object of fields to be tracked.
   */
  set(fieldsObject) {
    if (!fieldsObject || (Object.keys(fieldsObject) && Object.keys(fieldsObject).length === 0)) {
      console.warn("[GA] fieldsObject is required in set() and cannot be an empty object");
      return false;
    }
    runCommand("set", fieldsObject);
  },

  /**
   * Sets app related dimensions.
   */
  setAppDimensions() {
    const appConfig = {
      appName: "web",
      appId: "",
      appVersion: this.publicVersion,
      hostname: ""
    };
    this.set(appConfig);
  },

  /**
   * Sets custom dimension with new parental value.
   * @param {String} newParentalValue - New parental value to be set.
   */
  setNewParentalValue(newParentalValue) {
    this.set({ dimension9: newParentalValue });
  },

  /**
   * Sends a pageview tracking event.
   * @param {Object} data - Tracking data object. At least, contains data.path, data.pageType and
   * data.pageName in order to track the page.
   */
  async trackPage(data) {
    if (!data.path) {
      console.warn("[GA] path is required in trackPage() or cannot be an empty string");
      return false;
    }

    this.setAppDimensions();
    await setCustomDimensions.call(this, data.pageType, data.pageName, data.titleId);
    send({
      hitType: "pageview",
      page: data.path,
      ...(data.location && { location: data.location }),
      ...(data.title && { title: data.title })
    });
  },

  /**
   * Sends an event tracking event.
   * @param {Object} data - Event tracking data object. At least, contains data.category
   * and data.action in order to track the event.
   */
  trackEvent(data) {
    if (!data.category || !data.action) {
      console.warn("[GA] data.category and data.action are required in trackEvent() or cannot be an empty string");
      return false;
    }

    send({
      hitType: "event",
      eventCategory: data.category,
      eventAction: data.action,
      ...(data.label && { eventLabel: data.label }),
      ...(data.value && { eventValue: data.value })
    });
  }
};
